import { PaginateAbortError, PaginateAllPage } from './paginateAll';

export interface PaginateBatchedOptions<T> {
  signal?: AbortSignal;
  /** Number of pages fetched in parallel per batch. Defaults to 5. */
  batchSize?: number;
  /** Safety cap on the number of pages fetched. Defaults to 100. */
  maxPages?: number;
  /**
   * Fires after each batch (and after the discovery page) with the claims
   * accumulated so far, enabling incremental rendering. `page` is the highest
   * page included in `claimsSoFar`.
   */
  onBatch?: (claimsSoFar: T[], page: number, totalPages: number) => void;
}

const DEFAULT_BATCH_SIZE = 5;
const DEFAULT_MAX_PAGES = 100;

/**
 * Two-phase batched paginator.
 *
 * Phase 1 fetches page 1 to discover `totalPages`, then phase 2 fetches the
 * remaining pages in parallel batches of `batchSize`. This trades ~N serial
 * round-trips for ~ceil(N / batchSize) batches of parallel requests.
 *
 * The abort signal is honored both between batches and before each individual
 * page request inside a batch, so cancellation stays responsive mid-batch.
 */
export async function paginateAllBatched<T>(
  fetcher: (page: number) => Promise<PaginateAllPage<T> | null>,
  options: PaginateBatchedOptions<T> = {},
): Promise<T[]> {
  const {
    signal,
    batchSize = DEFAULT_BATCH_SIZE,
    maxPages = DEFAULT_MAX_PAGES,
    onBatch,
  } = options;

  if (signal?.aborted) {
    throw new PaginateAbortError();
  }

  // Phase 1 — discovery: fetch page 1 to learn how many pages exist.
  const firstPage = await fetcher(1);
  if (!firstPage) {
    return [];
  }

  const all: T[] = [...firstPage.data];
  const totalPages = firstPage.pagination
    ? Math.min(firstPage.pagination.totalPages, maxPages)
    : 1;

  onBatch?.(all, 1, totalPages);

  if (totalPages <= 1) {
    return all;
  }

  // Phase 2 — parallel batches for pages 2..totalPages.
  for (let start = 2; start <= totalPages; start += batchSize) {
    if (signal?.aborted) {
      throw new PaginateAbortError();
    }

    const end = Math.min(start + batchSize - 1, totalPages);
    const pageNumbers: number[] = [];
    for (let page = start; page <= end; page += 1) {
      pageNumbers.push(page);
    }

    const responses = await Promise.all(
      pageNumbers.map((page) => {
        if (signal?.aborted) {
          throw new PaginateAbortError();
        }
        return fetcher(page);
      }),
    );

    // Promise.all preserves order, so claims stay in page order.
    for (const response of responses) {
      if (response) {
        all.push(...response.data);
      }
    }

    onBatch?.(all, end, totalPages);
  }

  return all;
}
